/**
 * Extrae el valor escalar de una celda ExcelJS (rich text, fórmula, booleano, etc.).
 */
export function normalizarValorCeldaExcel(valor) {
  if (valor === null || valor === undefined) return '';
  if (typeof valor === 'boolean' || typeof valor === 'number') return valor;
  if (valor instanceof Date) return valor;
  if (typeof valor === 'object') {
    if (Array.isArray(valor.richText)) {
      return valor.richText.map((parte) => parte.text ?? '').join('');
    }
    if (valor.text !== undefined && valor.text !== null) return valor.text;
    if (valor.result !== undefined && valor.result !== null) return valor.result;
    if (valor.hyperlink && valor.text !== undefined) return valor.text;
  }
  return valor;
}

/**
 * Normaliza fechas leídas desde Excel (serial, Date, DD-MM-YYYY, YYYY-MM-DD).
 */
export function normalizarFechaImportacion(valor) {
  if (valor === null || valor === undefined || valor === '') return null;

  if (valor instanceof Date && !Number.isNaN(valor.getTime())) {
    const y = valor.getFullYear();
    const m = String(valor.getMonth() + 1).padStart(2, '0');
    const d = String(valor.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  if (typeof valor === 'string') {
    const trimmed = valor.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

    const dmy = trimmed.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (dmy) {
      const day = Number(dmy[1]);
      const month = Number(dmy[2]);
      const year = Number(dmy[3]);
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }
    }
  }

  const num = Number(valor);
  if (!Number.isNaN(num) && num > 0 && num < 1000000) {
    const excelEpochUtc = Date.UTC(1899, 11, 30);
    const date = new Date(excelEpochUtc + Math.floor(num) * 86400000);
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  const str = String(valor).trim();
  const parsed = new Date(str);
  if (!Number.isNaN(parsed.getTime())) {
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, '0');
    const d = String(parsed.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  return null;
}

export function extraerFechaYMD(valor) {
  if (valor == null || valor === '') return null;
  const s = String(valor).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  if (s.includes('T')) return s.split('T')[0];
  if (s.includes(' ')) return s.split(' ')[0];
  return null;
}

export function obtenerRegistrosHistorial(empleado) {
  const historial = empleado?.historial_laboral || empleado?.historialLaboral;
  const registros = Array.isArray(historial) && historial.length > 0
    ? historial
    : (empleado?.ultimo_historial ? [empleado.ultimo_historial] : []);

  return depurarRegistrosHistorial(registros, empleado);
}

/**
 * Historial de control (boleta interna). Independiente del PLAME:
 * un ingreso interno anterior (p. ej. 16/02) no debe recortarse al alta PLAME (25/07).
 */
export function obtenerRegistrosHistorialInterno(empleado) {
  const historial = empleado?.historial_laboral_interno
    || empleado?.historialLaboralInterno
    || empleado?.datosEmpleado?.historial_laboral_interno
    || empleado?.datosEmpleado?.historialLaboralInterno;
  if (Array.isArray(historial) && historial.length > 0) {
    return depurarRegistrosHistorial(historial, { ...empleado, estado: 'Activo' }, {
      conservarAbiertoSiHayCeseMismoIngreso: true,
      ignorarEstadoCesado: true,
    });
  }

  const fechaIngreso = extraerFechaYMD(
    empleado?.fecha_ingreso_interna
    || empleado?.fechaIngresoInterna
    || empleado?.datosEmpleado?.fecha_ingreso_interna
    || empleado?.datosEmpleado?.fechaIngresoInterna
    || empleado?.datosSalariales?.fechaIngresoInterna
  );
  if (!fechaIngreso) return [];
  return [{ fecha_ingreso: fechaIngreso, fecha_cese: null }];
}

export function construirEmpleadoParaHistorialInterno(empleado) {
  if (!empleado) return empleado;
  const registros = obtenerRegistrosHistorialInterno(empleado);
  if (registros.length) {
    return {
      ...empleado,
      estado: 'Activo',
      historial_laboral: registros,
    };
  }

  // Dual sin filas internas: no heredar el cese PLAME.
  // solo_interna no reabre PLAME: su vigencia es solo el historial interno.
  const tipo = tipoPlanillaDeEmpleado(empleado);
  if (tipo === 'dual') {
    const plame = construirEmpleadoParaHistorialLaboral(empleado);
    const abiertos = obtenerRegistrosHistorial(plame).map((r) => ({
      ...r,
      fecha_cese: null,
      motivo_cese: null,
    }));
    if (abiertos.length) {
      return { ...empleado, estado: 'Activo', historial_laboral: abiertos };
    }
  }

  return construirEmpleadoParaHistorialLaboral(empleado);
}

/**
 * Vista de boleta interna: tareos y días esperados salen del historial interno,
 * no del cese PLAME.
 */
export function vistaBoletaUsaCanalInterno(empleado, filtroTipoBoleta, fechaInicio, fechaFin) {
  const tipo = String(
    empleado?.tipo_planilla
    || empleado?.datosEmpleado?.tipo_planilla
    || empleado?.datosSalariales?.tipoPlanilla
    || ''
  ).toLowerCase();
  if (filtroTipoBoleta === 'interna' || tipo === 'solo_interna') return true;
  if (filtroTipoBoleta === 'plame') return false;
  if (fechaInicio && fechaFin && (tipo === 'dual' || tipo === '')) {
    if (
      !empleadoActivoDuranteRango(empleado, fechaInicio, fechaFin)
      && empleadoActivoDuranteRangoInterno(empleado, fechaInicio, fechaFin)
    ) {
      return true;
    }
  }
  return false;
}

function empleadoComoCanalInterno(empleado, registrosInterno) {
  const abiertos = (registrosInterno || []).filter((r) => r && !r.fecha_cese);
  const usar = abiertos.length ? abiertos : registrosInterno;
  return {
    ...empleado,
    estado: 'Activo',
    historial_laboral: usar,
    ultimo_historial: usar[0] || null,
  };
}

function empleadoVistaCanalInterno(empleado) {
  const construido = construirEmpleadoParaHistorialInterno(empleado);
  const registros = construido?.historial_laboral || [];
  if (!registros.length) return null;
  return empleadoComoCanalInterno(construido, registros);
}

export function empleadoActivoEnFechaPlameOInterno(empleado, fechaStr) {
  if (empleadoActivoEnFechaHistorial(empleado, fechaStr)) return true;
  const vistaInterno = empleadoVistaCanalInterno(empleado);
  if (!vistaInterno) return false;
  return empleadoActivoEnFechaHistorial(vistaInterno, fechaStr);
}

export function empleadoActivoDuranteRangoInterno(empleado, fechaInicio, fechaFin) {
  const vistaInterno = empleadoVistaCanalInterno(empleado);
  if (!vistaInterno) return false;
  return empleadoActivoDuranteRango(vistaInterno, fechaInicio, fechaFin);
}

export function empleadoActivoDuranteRangoPlameOInterno(empleado, fechaInicio, fechaFin) {
  return empleadoActivoDuranteRango(empleado, fechaInicio, fechaFin)
    || empleadoActivoDuranteRangoInterno(empleado, fechaInicio, fechaFin);
}

export function tipoPlanillaDeEmpleado(empleado) {
  return String(
    empleado?.tipo_planilla
    || empleado?.datosEmpleado?.tipo_planilla
    || empleado?.datosSalariales?.tipoPlanilla
    || ''
  ).toLowerCase();
}

/**
 * Vigencia en el periodo según el tipo de planilla del trabajador.
 * solo_interna: solo historial interno (un PLAME abierto residual no lo mantiene).
 * dual: PLAME o interno.
 * plame / resto: solo PLAME.
 */
export function empleadoActivoDuranteRangoSegunTipoPlanilla(empleado, fechaInicio, fechaFin) {
  const tipo = tipoPlanillaDeEmpleado(empleado);
  if (tipo === 'solo_interna') {
    return empleadoActivoDuranteRangoInterno(empleado, fechaInicio, fechaFin);
  }
  if (tipo === 'dual') {
    return empleadoActivoDuranteRangoPlameOInterno(empleado, fechaInicio, fechaFin);
  }
  return empleadoActivoDuranteRango(empleado, fechaInicio, fechaFin);
}

/**
 * Qué boletas corresponden al periodo: un dual cesado en PLAME
 * no debe mostrar ni totalizar PLAME si solo la interna cubre el mes.
 */
export function canalesBoletaEnPeriodo(empleado, fechaInicio, fechaFin) {
  const tipo = tipoPlanillaDeEmpleado(empleado);
  if (!fechaInicio || !fechaFin) {
    if (tipo === 'solo_interna') return { plame: false, interna: true };
    if (tipo === 'dual') return { plame: true, interna: true };
    return { plame: true, interna: false };
  }
  const activoPlame = empleadoActivoDuranteRango(empleado, fechaInicio, fechaFin);
  const registrosInterno = obtenerRegistrosHistorialInterno(empleado);
  const activoInterno = registrosInterno.length
    ? empleadoActivoDuranteRango(
        { ...empleado, estado: 'Activo', historial_laboral: registrosInterno },
        fechaInicio,
        fechaFin
      )
    : empleadoActivoDuranteRangoInterno(empleado, fechaInicio, fechaFin);
  if (tipo === 'solo_interna') {
    return { plame: false, interna: activoInterno };
  }
  if (tipo === 'dual') {
    return { plame: activoPlame, interna: activoInterno };
  }
  return { plame: activoPlame, interna: false };
}

export function empleadoActivoEnFechaSegunTipoPlanilla(empleado, fechaStr) {
  const tipo = tipoPlanillaDeEmpleado(empleado);
  if (tipo === 'solo_interna') {
    const vistaInterno = empleadoVistaCanalInterno(empleado);
    return Boolean(vistaInterno && empleadoActivoEnFechaHistorial(vistaInterno, fechaStr));
  }
  if (empleadoActivoEnFechaHistorial(empleado, fechaStr)) return true;
  if (tipo === 'dual') {
    const vistaInterno = empleadoVistaCanalInterno(empleado);
    return Boolean(vistaInterno && empleadoActivoEnFechaHistorial(vistaInterno, fechaStr));
  }
  return false;
}

function historialTienePeriodoAbierto(registros) {
  return (Array.isArray(registros) ? registros : []).some((r) => r && !r.fecha_cese);
}

/**
 * Estado vigente del canal PLAME. Independiente del historial interno.
 */
export function estadoPlameDeEmpleado(empleado) {
  const registros = empleado?.historial_laboral || empleado?.historialLaboral || [];
  if (Array.isArray(registros) && registros.length > 0) {
    return historialTienePeriodoAbierto(registros) ? 'Activo' : 'Cesado';
  }
  const estado = String(empleado?.estado || '').trim();
  if (estado === 'Activo' || estado === 'Cesado') return estado;
  return null;
}

/**
 * Estado vigente del canal interno. Independiente del cese PLAME.
 */
export function estadoInternoDeEmpleado(empleado) {
  const registros = empleado?.historial_laboral_interno
    || empleado?.historialLaboralInterno
    || empleado?.datosEmpleado?.historial_laboral_interno
    || [];
  if (Array.isArray(registros) && registros.length > 0) {
    return historialTienePeriodoAbierto(registros) ? 'Activo' : 'Cesado';
  }
  const fechaIngreso = empleado?.fecha_ingreso_interna
    || empleado?.fechaIngresoInterna
    || empleado?.datosEmpleado?.fecha_ingreso_interna;
  if (fechaIngreso) return 'Activo';
  if (tipoPlanillaDeEmpleado(empleado) === 'dual') return 'Activo';
  return null;
}

export function empleadoVigenteEnAlgunaPlanilla(empleado) {
  const tipo = tipoPlanillaDeEmpleado(empleado);
  if (tipo === 'solo_interna') {
    return estadoInternoDeEmpleado(empleado) === 'Activo';
  }
  if (tipo === 'dual') {
    return estadoPlameDeEmpleado(empleado) === 'Activo'
      || estadoInternoDeEmpleado(empleado) === 'Activo';
  }
  return estadoPlameDeEmpleado(empleado) === 'Activo';
}

/**
 * Elimina registros abiertos duplicados y, si el empleado está cesado,
 * solo considera periodos con fecha de cese.
 */
export function depurarRegistrosHistorial(registros, empleado = null, opciones = {}) {
  if (!Array.isArray(registros) || registros.length === 0) return [];

  const ingresosConCese = new Set(
    registros
      .filter((h) => h?.fecha_cese)
      .map((h) => extraerFechaYMD(h.fecha_ingreso))
      .filter(Boolean)
  );

  let depurados = registros.filter((h) => {
    const ingreso = extraerFechaYMD(h?.fecha_ingreso);
    if (!ingreso) return false;
    if (
      !opciones.conservarAbiertoSiHayCeseMismoIngreso
      && !h.fecha_cese
      && ingresosConCese.has(ingreso)
    ) {
      return false;
    }
    return true;
  });

  const estado = String(empleado?.estado || '').trim().toLowerCase();
  if (estado === 'cesado' && !opciones.ignorarEstadoCesado) {
    const conCese = depurados.filter((h) => h.fecha_cese);
    if (conCese.length > 0) {
      depurados = conCese;
    } else {
      const ingresoFlat = extraerFechaYMD(
        empleado?.fecha_ingreso_actual
        || empleado?.fecha_ingreso
        || empleado?.fechaIngreso
      );
      const ceseFlat = extraerFechaYMD(
        empleado?.fecha_cese_actual
        || empleado?.fecha_cese
        || empleado?.fechaCese
      );
      if (ingresoFlat && ceseFlat) {
        depurados = [{ id: 0, fecha_ingreso: ingresoFlat, fecha_cese: ceseFlat }];
      } else {
        depurados = [];
      }
    }
  }

  return depurados;
}

/**
 * Misma lógica que Empleado::getPeriodoVigenteEnFecha (backend).
 */
export function obtenerPeriodoVigenteEnFecha(empleado, fechaStr) {
  const fecha = extraerFechaYMD(fechaStr);
  if (!fecha || !empleado) return null;

  const registros = obtenerRegistrosHistorial(empleado);
  if (!registros.length) return null;

  const vigente = registros
    .filter((h) => h?.fecha_ingreso && extraerFechaYMD(h.fecha_ingreso) <= fecha)
    .sort((a, b) => {
      const ia = extraerFechaYMD(a.fecha_ingreso);
      const ib = extraerFechaYMD(b.fecha_ingreso);
      if (ib !== ia) return ib.localeCompare(ia);
      return (b.id || 0) - (a.id || 0);
    })[0];

  if (!vigente) return null;

  if (vigente.fecha_cese && extraerFechaYMD(vigente.fecha_cese) < fecha) {
    return null;
  }

  return vigente;
}

/**
 * Período laboral que solapa con el rango de la planilla (mayor fecha_ingreso entre solapados).
 */
export function obtenerPeriodoLaboralEnRango(empleado, fechaInicio, fechaFin) {
  const inicio = extraerFechaYMD(fechaInicio);
  const fin = extraerFechaYMD(fechaFin);
  if (!inicio || !fin || !empleado) return null;

  const solapados = obtenerRegistrosHistorial(empleado).filter((h) => {
    if (!h?.fecha_ingreso) return false;
    const ingreso = extraerFechaYMD(h.fecha_ingreso);
    const cese = h.fecha_cese ? extraerFechaYMD(h.fecha_cese) : null;
    return ingreso <= fin && (!cese || cese >= inicio);
  });

  if (!solapados.length) return null;

  return solapados.sort((a, b) => {
    const ia = extraerFechaYMD(a.fecha_ingreso);
    const ib = extraerFechaYMD(b.fecha_ingreso);
    if (ib !== ia) return ib.localeCompare(ia);
    return (b.id || 0) - (a.id || 0);
  })[0];
}

export function obtenerFechasLaboralesEfectivasEnRango(empleado, fechaInicioPlanilla, fechaFinPlanilla) {
  const inicioPlanilla = extraerFechaYMD(fechaInicioPlanilla);
  const finPlanilla = extraerFechaYMD(fechaFinPlanilla);
  const periodo = obtenerPeriodoLaboralEnRango(empleado, inicioPlanilla, finPlanilla);

  if (!periodo) {
    return { fechaIngreso: null, fechaCese: null, periodo: null };
  }

  const ingreso = extraerFechaYMD(periodo.fecha_ingreso);
  const cese = periodo.fecha_cese ? extraerFechaYMD(periodo.fecha_cese) : null;
  const fechaIngresoEfectiva = ingreso > inicioPlanilla ? ingreso : inicioPlanilla;
  let fechaCeseEfectiva = finPlanilla;
  if (cese && cese < finPlanilla) {
    fechaCeseEfectiva = cese;
  }

  return {
    fechaIngreso: fechaIngresoEfectiva,
    fechaCese: fechaCeseEfectiva,
    periodo,
  };
}

export function empleadoActivoDuranteRango(empleado, fechaInicio, fechaFin) {
  const periodo = obtenerPeriodoLaboralEnRango(empleado, fechaInicio, fechaFin);
  if (!periodo) return false;
  if (periodo.motivo_cese === 'No se presentó a laborar') return false;

  const { fechaIngreso, fechaCese } = obtenerFechasLaboralesEfectivasEnRango(
    empleado,
    fechaInicio,
    fechaFin
  );
  if (!fechaIngreso || !fechaCese || fechaIngreso > fechaCese) return false;

  return true;
}

/**
 * Vigencia en el periodo de planilla usando historial PLAME normalizado (snapshot o historial completo).
 */
export function empleadoVigenteEnRangoPlanilla(empleado, fechaInicio, fechaFin) {
  if (!empleado || !fechaInicio || !fechaFin) return false;
  const laboral = construirEmpleadoParaHistorialLaboral(empleado);
  return empleadoActivoDuranteRangoSegunTipoPlanilla(laboral, fechaInicio, fechaFin);
}

/**
 * Arma historial laboral desde snapshot de planilla cerrada cuando no viene historial_laboral completo.
 */
export function construirEmpleadoParaHistorialLaboral(empleado) {
  if (!empleado) return empleado;
  if (obtenerRegistrosHistorial(empleado).length > 0) return empleado;

  const fechaIngreso = empleado.fecha_ingreso_actual
    || empleado.fechaIngreso
    || empleado.fecha_ingreso;
  const fechaCese = empleado.fecha_cese_actual
    || empleado.fechaCese
    || empleado.fecha_cese
    || null;

  if (!fechaIngreso) return empleado;

  return {
    ...empleado,
    historial_laboral: [{
      id: 0,
      fecha_ingreso: fechaIngreso,
      fecha_cese: fechaCese,
    }],
  };
}

export function tareoEnPeriodoLaboralEfectivo(fechaTareo, empleado, fechaInicioPlanilla, fechaFinPlanilla) {
  const fecha = extraerFechaYMD(fechaTareo);
  if (!fecha) return false;

  const { fechaIngreso, fechaCese } = obtenerFechasLaboralesEfectivasEnRango(
    empleado,
    fechaInicioPlanilla,
    fechaFinPlanilla
  );

  if (!fechaIngreso || !fechaCese) return false;
  return fecha >= fechaIngreso && fecha <= fechaCese;
}

/**
 * Misma lógica que Empleado::getPeriodoVigenteEnFecha (backend).
 */
export function empleadoActivoEnFechaHistorial(empleado, fechaStr) {
  if (!fechaStr || !empleado) return false;
  return obtenerPeriodoVigenteEnFecha(empleado, fechaStr) !== null;
}
